import React from "react";
import {
  AbsoluteFill,
  Easing,
  interpolate,
  spring,
  useCurrentFrame,
  useVideoConfig,
} from "remotion";
import { useCopy } from "../copy";
import { LOOK, SAFE, TYPE } from "../look";
import { useStage } from "../stage";
import { Words } from "../ui/Words";
import { Sfx, Ticks } from "../../b2b/ui/Sfx";

/**
 * Scene 2 — why your recommendation matters. One number, sourced
 * (Nielsen, cited in the blog post "Nous sommes tous des influenceurs").
 * Hard cut from the light chat to full Frak blue on the beat.
 */
export const Trust: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const copy = useCopy();
  const stage = useStage();

  const count = interpolate(frame, [0, 22], [0, copy.trust.stat], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
    easing: Easing.bezier(0.16, 1, 0.3, 1),
  });
  const pop = spring({ frame, fps, config: { damping: 13, stiffness: 200, mass: 0.7 } });
  const kicker = spring({
    frame: frame - 44,
    fps,
    config: { damping: 14, stiffness: 220, mass: 0.6 },
  });

  return (
    <AbsoluteFill
      style={{
        background: LOOK.blue,
        alignItems: "center",
        justifyContent: "center",
        padding: SAFE,
        gap: 28,
      }}
    >
      <div
        style={{
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          fontSize: 300,
          lineHeight: 0.9,
          letterSpacing: -14,
          color: LOOK.white,
          fontVariantNumeric: "tabular-nums",
          scale: interpolate(pop, [0, 1], [0.6, 1]),
          opacity: Math.min(1, pop * 3),
        }}
      >
        {Math.round(count)}
        <span style={{ fontSize: 200, letterSpacing: -6 }}>{copy.trust.statSuffix}</span>
      </div>

      <Words
        text={copy.trust.line}
        start={10}
        stagger={2}
        fontSize={62}
        color={LOOK.white}
        lineHeight={1.1}
        style={{ maxWidth: stage.shape === "wide" ? 1300 : 860 }}
      />

      <div
        style={{
          marginTop: 22,
          padding: "16px 34px",
          borderRadius: 999,
          background: LOOK.white,
          color: LOOK.blue,
          fontFamily: LOOK.display,
          fontWeight: LOOK.bold,
          fontSize: TYPE.support,
          letterSpacing: -1,
          scale: kicker,
          rotate: `${interpolate(kicker, [0, 1], [-8, -2])}deg`,
        }}
      >
        {copy.trust.kicker}
      </div>

      <div
        style={{
          position: "absolute",
          bottom: 64 + stage.safeBottom,
          fontFamily: LOOK.body,
          fontSize: TYPE.legal,
          color: LOOK.white,
          opacity: interpolate(frame, [58, 66], [0, 0.75], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          }),
        }}
      >
        {copy.trust.source}
      </div>

      <Ticks from={2} to={14} every={2} volume={0.7} />
      <Sfx sound="pop" at={46} volume={0.8} />
    </AbsoluteFill>
  );
};
